import api from '@/api'
import { decryptCalibration } from '@/util/crypto'
const state = {
  mixers: [],

  currentMixer: null,

  models: [],

  currentModel: null,

  calibrations: [],

  loading: {
    mixers: false,
    models: false,
    calibrations: false
  },

  errors: {
    mixers: null,
    models: null,
    calibrations: null
  }
}

const mutations = {
  SET_MIXERS (state, mixers) {
    state.mixers = mixers
  },

  SET_CURRENT_MIXER (state, mixer) {
    state.currentMixer = mixer
  },

  ADD_MIXER (state, mixer) {
    state.mixers.push(mixer)
  },

  UPDATE_MIXER (state, updatedMixer) {
    if (!updatedMixer || !updatedMixer.id) {
      console.error('Invalid mixer data in UPDATE_MIXER mutation:', updatedMixer)
      return
    }

    const index = state.mixers.findIndex(m => m.id === updatedMixer.id)
    if (index !== -1) {
      state.mixers.splice(index, 1, updatedMixer)
    } else {
      console.warn('Mixer not found in array for update:', updatedMixer.id)
    }
    if (state.currentMixer && state.currentMixer.id === updatedMixer.id) {
      state.currentMixer = updatedMixer
      console.log('Current mixer updated')
    }
  },

  DELETE_MIXER (state, mixerId) {
    state.mixers = state.mixers.filter(m => m.id !== mixerId)

    if (state.currentMixer && state.currentMixer.id === mixerId) {
      state.currentMixer = null
    }
  },

  SET_MODELS (state, models) {
    state.models = models
  },

  SET_CURRENT_MODEL (state, model) {
    state.currentModel = model
  },

  ADD_MODEL (state, model) {
    state.models.push(model)
  },

  UPDATE_MODEL (state, updatedModel) {
    const index = state.models.findIndex(m => m.id === updatedModel.id)
    if (index !== -1) {
      state.models.splice(index, 1, updatedModel)
    }

    if (state.currentModel && state.currentModel.id === updatedModel.id) {
      state.currentModel = updatedModel
    }
  },

  DELETE_MODEL (state, modelId) {
    state.models = state.models.filter(m => m.id !== modelId)

    if (state.currentModel && state.currentModel.id === modelId) {
      state.currentModel = null
    }
  },

  SET_CALIBRATIONS (state, calibrations) {
    state.calibrations = calibrations
  },

  ADD_CALIBRATION (state, calibration) {
    state.calibrations.push(calibration)
  },

  UPDATE_CALIBRATION (state, updatedCalibration) {
    const index = state.calibrations.findIndex(c => c.id === updatedCalibration.id)
    if (index !== -1) {
      state.calibrations.splice(index, 1, updatedCalibration)
    }
  },

  DELETE_CALIBRATION (state, calibrationId) {
    state.calibrations = state.calibrations.filter(c => c.id !== calibrationId)
  },

  SET_LOADING (state, { type, loading }) {
    state.loading[type] = loading
  },

  SET_ERROR (state, { type, error }) {
    state.errors[type] = error
  },

  CLEAR_MODELS (state) {
    state.models = []
    state.currentModel = null
  },

  CLEAR_CALIBRATIONS (state) {
    state.calibrations = []
  },

  RESET_STATE (state) {
    state.mixers = []
    state.currentMixer = null
    state.models = []
    state.currentModel = null
    state.calibrations = []
    state.loading = {
      mixers: false,
      models: false,
      calibrations: false
    }
    state.errors = {
      mixers: null,
      models: null,
      calibrations: null
    }
  }
}

const actions = {
  async loadMixers ({ commit }, params = {}) {
    if (state.loading.mixers) {
      return state.mixers
    }
    if (state.mixers.length > 0 && !params.forceReload) {
      return state.mixers
    }
    commit('SET_LOADING', { type: 'mixers', loading: true })
    commit('SET_ERROR', { type: 'mixers', error: null })

    try {
      const response = await api.dashboard.mixers.list(params)
      const mixers = response.data || response.results || []
      commit('SET_MIXERS', mixers)
      return mixers
    } catch (error) {
      commit('SET_ERROR', { type: 'mixers', error })
      throw error
    } finally {
      commit('SET_LOADING', { type: 'mixers', loading: false })
    }
  },

  async loadMixer ({ commit }, mixerId) {
    try {
      const response = await api.dashboard.mixers.getById(mixerId)
      const mixer = response.data
      commit('SET_CURRENT_MIXER', mixer)
      return mixer
    } catch (error) {
      commit('SET_ERROR', { type: 'mixers', error })
      throw error
    }
  },

  async createMixer ({ commit }, mixerData) {
    try {
      const response = await api.dashboard.mixers.create(mixerData)

      const newMixer = response.data

      if (newMixer) {
        commit('ADD_MIXER', newMixer)
      } else {
        console.error('No mixer data in response')
      }

      return newMixer
    } catch (error) {
      console.error('Error in createMixer action:', error)
      commit('SET_ERROR', { type: 'mixers', error })
      throw error
    }
  },

  async updateMixer ({ commit }, { id, data }) {
    if (!id) {
      const error = new Error('ID is required for updateMixer')
      console.error(error.message)
      commit('SET_ERROR', { type: 'mixers', error })
      throw error
    }

    if (!data) {
      const error = new Error('Data is required for updateMixer')
      console.error(error.message)
      commit('SET_ERROR', { type: 'mixers', error })
      throw error
    }

    try {
      const response = await api.dashboard.mixers.updateById(id, data)
      const updatedMixer = response.data

      if (!updatedMixer) {
        throw new Error('No data returned from API')
      }

      commit('UPDATE_MIXER', updatedMixer)
      return updatedMixer
    } catch (error) {
      console.error('Error in updateMixer action:', error)
      commit('SET_ERROR', { type: 'mixers', error })
      throw error
    }
  },

  async deleteMixer ({ commit }, mixerId) {
    try {
      await api.dashboard.mixers.deleteById(mixerId)
      commit('DELETE_MIXER', mixerId)
      return true
    } catch (error) {
      commit('SET_ERROR', { type: 'mixers', error })
      throw error
    }
  },

  async loadModels ({ commit }, mixerId) {
    commit('SET_LOADING', { type: 'models', loading: true })
    commit('SET_ERROR', { type: 'models', error: null })

    try {
      const response = await api.dashboard.mixers.listModelsByBrand({ brand: mixerId })
      const models = response.data || response.results || []
      commit('SET_MODELS', models)
      return models
    } catch (error) {
      commit('SET_ERROR', { type: 'models', error })
      throw error
    } finally {
      commit('SET_LOADING', { type: 'models', loading: false })
    }
  },

  async loadModel ({ commit }, modelId) {
    try {
      const response = await api.dashboard.mixers.getModelById(modelId)
      const model = response.data
      commit('SET_CURRENT_MODEL', model)
      return model
    } catch (error) {
      commit('SET_ERROR', { type: 'models', error })
      throw error
    }
  },

  async createModel ({ commit }, modelData) {
    try {
      const response = await api.dashboard.mixers.createModel(modelData)
      const newModel = response.data
      commit('ADD_MODEL', newModel)
      return newModel
    } catch (error) {
      commit('SET_ERROR', { type: 'models', error })
      throw error
    }
  },

  async updateModel ({ commit }, { id, data }) {
    try {
      const response = await api.dashboard.mixers.updateModel(id, data)
      const updatedModel = response.data
      commit('UPDATE_MODEL', updatedModel)
      return updatedModel
    } catch (error) {
      commit('SET_ERROR', { type: 'models', error })
      throw error
    }
  },

  async deleteModel ({ commit }, modelId) {
    try {
      await api.dashboard.mixers.deleteModel(modelId)
      commit('DELETE_MODEL', modelId)
      return true
    } catch (error) {
      commit('SET_ERROR', { type: 'models', error })
      throw error
    }
  },

  async loadCalibrations ({ commit }, modelId) {
    commit('SET_LOADING', { type: 'calibrations', loading: true })
    commit('SET_ERROR', { type: 'calibrations', error: null })

    try {
      const response = await api.dashboard.mixers.listCalibrationsByModel({ model: modelId })
      let calibrations = response.data || response.results || []

      if (calibrations.length > 0) {
        calibrations = await Promise.all(
          calibrations.map(async (calibration) => {
            try {
              const decryptedCalibration = await decryptCalibration(calibration.calibration)
              return {
                ...calibration,
                calibration: decryptedCalibration,
                encrypted_calibration: calibration.calibration
              }
            } catch (error) {
              console.error('Failed to decrypt calibration:', error)
              return {
                ...calibration,
                calibration: '[Помилка дешифрування]',
                encrypted_calibration: calibration.calibration
              }
            }
          })
        )
      }

      commit('SET_CALIBRATIONS', calibrations)
      return calibrations
    } catch (error) {
      commit('SET_ERROR', { type: 'calibrations', error })
      throw error
    } finally {
      commit('SET_LOADING', { type: 'calibrations', loading: false })
    }
  },

  async createCalibration ({ commit }, calibrationData) {
    try {
      const response = await api.dashboard.mixers.createCalibration(calibrationData)
      const newCalibration = response.data

      const decryptedCalibration = await decryptCalibration(newCalibration.calibration)

      const calibrationToStore = {
        ...newCalibration,
        calibration: decryptedCalibration,
        encrypted_calibration: newCalibration.calibration
      }

      commit('ADD_CALIBRATION', calibrationToStore)
      return calibrationToStore
    } catch (error) {
      commit('SET_ERROR', { type: 'calibrations', error })
      throw error
    }
  },

  async updateCalibration ({ commit }, { id, data }) {
    try {
      const response = await api.dashboard.mixers.updateCalibration(id, data)
      const updatedCalibration = response.data

      const decryptedCalibration = await decryptCalibration(updatedCalibration.calibration)

      const calibrationToStore = {
        ...updatedCalibration,
        calibration: decryptedCalibration,
        encrypted_calibration: updatedCalibration.calibration
      }

      commit('UPDATE_CALIBRATION', calibrationToStore)
      return calibrationToStore
    } catch (error) {
      commit('SET_ERROR', { type: 'calibrations', error })
      throw error
    }
  },

  async deleteCalibration ({ commit }, calibrationId) {
    try {
      await api.dashboard.mixers.deleteCalibration(calibrationId)
      commit('DELETE_CALIBRATION', calibrationId)
      return true
    } catch (error) {
      commit('SET_ERROR', { type: 'calibrations', error })
      throw error
    }
  },

  clearModels ({ commit }) {
    commit('CLEAR_MODELS')
  },

  clearCalibrations ({ commit }) {
    commit('CLEAR_CALIBRATIONS')
  },

  resetState ({ commit }) {
    commit('RESET_STATE')
  }
}

const getters = {
  allMixers: state => state.mixers,
  currentMixer: state => state.currentMixer,
  mixerById: state => id => state.mixers.find(mixer => mixer.id === parseInt(id)),
  mixerName: state => state.currentMixer && state.currentMixer.name ? state.currentMixer.name : null,
  mixersCount: state => state.mixers.length,

  allModels: state => state.models,
  currentModel: state => state.currentModel,
  modelById: state => id => state.models.find(model => model.id === parseInt(id)),
  modelName: state => state.currentModel && state.currentModel.name ? state.currentModel.name : null,
  modelsCount: state => state.models.length,

  allCalibrations: state => state.calibrations,
  calibrationById: state => id => state.calibrations.find(cal => cal.id === parseInt(id)),
  calibrationsCount: state => state.calibrations.length,

  isLoadingMixers: state => state.loading.mixers,
  isLoadingModels: state => state.loading.models,
  isLoadingCalibrations: state => state.loading.calibrations,
  isLoading: state => Object.values(state.loading).some(loading => loading),

  mixersError: state => state.errors.mixers,
  modelsError: state => state.errors.models,
  calibrationsError: state => state.errors.calibrations,
  hasErrors: state => Object.values(state.errors).some(error => error !== null)
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
